// src/helpers/notifications/notificationConfig.ts

import { eventsTemplates } from './templates/events';
import { messagesTemplates } from './templates/messages';
// import { newsTemplates } from './templates/news';
import { makeCrudTemplates } from './templates/crud';
import { updatesManualTemplates } from './templates/updates';
import { CHANNELS } from './channels';
import { TABS } from './tabs';

export type NotificationType = 'events' | 'messages' | 'updates' | 'news';
export type TabType = NotificationType | 'all';

export interface TemplateMeta {
	key: string;
	label: string;
	type: NotificationType;
	icon: string;
	title: (data: Record<string, any>) => string;
	subtitle?: (data: Record<string, any>) => string;
	supportsReminder?: boolean;
}

export interface Category {
	key: NotificationType;
	label: string;
	description: string;
	templates: TemplateMeta[];
}

export const CATEGORIES: Category[] = [
	{
		key: 'events',
		label: 'Events',
		description: 'Meetings, tasks and other events.',
		templates: eventsTemplates,
	},
	{
		key: 'messages',
		label: 'Messages',
		description: 'Direct messages, mentions, announcements.',
		templates: messagesTemplates,
	},
	{
		key: 'updates',
		label: 'Updates',
		description: 'Creates, changes, deletions and workflow moves.',
		templates: [
			...updatesManualTemplates, 	   // manual updates
			...makeCrudTemplates(),      // auto-generated CRUD for your models
		],
	},
	// {
	// 	key: 'news',
	// 	label: 'News',
	// 	description: 'Company news, blog posts and vendor updates.',
	// 	templates: newsTemplates,
	// },
];

// helpers for lookup & rendering:
export function getMeta(templateKey: string): TemplateMeta | undefined {
	return CATEGORIES.flatMap(c => c.templates).find(t => t.key === templateKey);
}

export function render(
	templateKey: string,
	data: Record<string, string>
): { title: string; subtitle?: string } {
	const m = getMeta(templateKey);
	if (!m) return { title: templateKey };
	return { title: m.title(data), subtitle: m.subtitle?.(data) };
}

export function getClickAction(templateKey: string, data: Record<string, string>): string {
	const model = data.model;
	const recordId = data.record_id;
	const childModel = data.child_model;
	const childRecordId = data.child_record_id;

	// Base path from model and record
	let basePath = model && recordId ? `/${model}/${recordId}` : "/students";

	// Append child query params if present
	const queryParams = new URLSearchParams();
	if (childModel) queryParams.set("context_model", childModel);
	if (childRecordId) queryParams.set("context_id", childRecordId);

	const withQuery = queryParams.toString() ? `${basePath}?${queryParams.toString()}` : basePath;

	switch (templateKey) {
		case "meeting.scheduled":
		case "meeting.updated":
		case "meeting.cancelled":
		case "meeting.reminder":
		case "task.assigned":
		case "task.due":
		case "task.completed":
		case "message.received":
		case "mention.in_comment":
			return withQuery;

		case "group.announcement":
			return model && recordId ? `/${model}/${recordId}` : "/announcements";

		case "profile.updated":
		case "password.changed":
		case "subscription.renewed":
			return "/settings/profile";

		case "feature.available":
			return "/features";

		case "news.published":
			return recordId ? `/news/${recordId}` : "/news";

		case "blog.posted":
		case "vendor.update":
			return data.url || data.doc_url || "#";

		default:
			return withQuery || "/notifications";
	}
}

/** Suffix used to name the daily‐digest variants */
export const DIGEST_SUFFIX = '.digest';

/** True if this key is a digest‐only template */
export function isDigestTemplate(key: string): boolean {
  return key.endsWith(DIGEST_SUFFIX);
}

/**
 * If there’s a digest sibling for the given base key,
 * return “<baseKey>.digest”; otherwise undefined.
 */
export function getDigestTemplateKey(baseKey: string): string | undefined {
  const candidate = `${baseKey}${DIGEST_SUFFIX}`;
  return CATEGORIES
    .flatMap(c => c.templates)
    .find(t => t.key === candidate)
    ? candidate
    : undefined;
}


export { CHANNELS, TABS };
